import { Fragment, useState, MouseEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Popover, Typography, useTheme } from '@mui/material'

import { useUnreviewedScansCount } from 'src/hooks'
import { ReviewedCount } from './reviewed-count.component'
import { CountChip } from 'src/components'
import { ETranslationNs } from 'src/types'
import { ROUTES } from 'src/constants'

import useStyles from './styles'

interface INavbarDesktopProps {
  title: string
  path: string
  icon: any
  iconMode?: string
  isActive?: boolean
  withDivider?: boolean
  isSkip: boolean
  pathname: string[]
  isScanHistory: boolean
  isOpen: boolean
  handleClick?: () => void
  disabled?: boolean
}

const NavbarItem = ({
  title,
  path,
  icon,
  iconMode,
  isActive,
  withDivider,
  isSkip,
  pathname,
  isScanHistory,
  isOpen,
  handleClick,
  disabled = false,
}: INavbarDesktopProps) => {
  const [t] = useTranslation(ETranslationNs.Navbar)

  const location = useLocation()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const theme = useTheme()
  const classes = useStyles({ isOpen, iconMode })

  const { data: unreviewedCount, isLoading: isUnreviewedLoading } = useUnreviewedScansCount()

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl) && !isOpen

  return (
    <Fragment>
      <Link
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        style={{
          display: isSkip ? 'none' : 'flex',
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.5 : 1,
        }}
        to={disabled ? location.pathname : path || location.pathname}
        onClick={() => {
          if (!disabled && handleClick) {
            handleClick()
          }
        }}
        className={`${classes.link} ${isScanHistory ? 'scan-history-link' : ''}`}
      >
        <Box sx={{ position: 'relative' }} width={25}>
          {icon({ isActive: path && pathname.includes(path) })}
          {isScanHistory && !isOpen && (
            <Box
              onMouseEnter={handlePopoverClose}
              onMouseLeave={() => {
                const linkElement = document.querySelector<HTMLAnchorElement>('.scan-history-link')
                if (linkElement) {
                  setAnchorEl(linkElement)
                }
              }}
              sx={{ position: 'absolute', right: '-12px', bottom: '-2px' }}
            >
              <CountChip
                count={unreviewedCount?.count || 0}
                hoverText={t('scan-history-unapproved-notification-hover-popup.v')}
                isLoading={isUnreviewedLoading}
                size="small"
              />
            </Box>
          )}
        </Box>
        <div
          className={`${classes.titleWrapper} ${
            ((path && pathname.includes(path)) || isActive) && classes.activeColor
          }`}
          style={{ position: 'relative' }}
        >
          {t(title)}
          <>{isScanHistory && <ReviewedCount />}</>
          {path === `/${ROUTES.USER}/${ROUTES.SCAN_TO_EARN}` && (
            <Typography
              sx={{
                position: 'absolute',
                top: '0px',
                right: '-32px',
                fontStyle: 'italic',
                fontSize: '9px',
                fontWeight: 700,
                color: theme.palette.custom.primary,
              }}
            >
              ALPHA
            </Typography>
          )}
        </div>
      </Link>
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        disableScrollLock
      >
        <div className={classes.textWrapper}>
          <Typography className={classes.title}>{t(title)}</Typography>
        </div>
      </Popover>
      {withDivider && !isSkip && <Divider variant="fullWidth" flexItem />}
    </Fragment>
  )
}

export { NavbarItem }
